import React, { useContext, useRef } from 'react';
import { Button, Col, Divider, Row, Tooltip } from 'antd';
import { Input, Select } from 'formik-antd';
import { has, isNil } from 'lodash';
import bankersRounding from 'bankers-rounding';
import { FormikProps, useFormikContext } from 'formik';
import cx from 'classnames';
import { useDidUpdate } from 'rooks';
import moment from 'moment';

import FormItem from 'components/formik/FormItem';
import {
  getFinancialWarningMessages,
  getOvertimeOptions,
  getPayTypeOptions,
} from 'utils/assignmentUtils';
import InputNumber from 'components/formik/InputNumber';
import {
  CustomerMspOrVmsOverviewDto,
  OvertimeEnum,
} from 'api/backofficeApiClient';
import { getFormattedNumber } from 'utils/numberUtils';
import {
  DT_PAY_RATE_MULTIPLIER,
  FinancialInfoCalculator,
  OVERTIME_MULTIPLIER,
} from '../calculationUtils';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { FinancialInfoFormValues, MultiRateFormValues } from '../FinancialInfo';
import { getDecimalSafeValue } from 'utils/mathUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { MULTI_RATES_KEY } from 'hooks/useFinancialInfoTabs';
import FinancialSchedulerContext from 'components/FinancialSchedulerContext';
import { findPTOMultiRate } from 'utils/multiRates/multiRatesUtils';

import styles from '../FinancialInfo.module.less';

type Props = {
  formRef: any;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  isViewDTBillRateDtPayRateFields: boolean;
  handleOpenFinancialInfoSchedulerModal: () => void;
  isTeammate?: boolean;
  setOvertimeValue: (value: OvertimeEnum | null) => void;
};

export default function FinancialInfoDetails(props: Props) {
  const {
    formRef,
    mspOrVmsOverviewInfo,
    isViewDTBillRateDtPayRateFields,
    handleOpenFinancialInfoSchedulerModal,
    isTeammate,
    setOvertimeValue,
  } = props;
  const {
    workSiteWorkLocation,
    predefinedFinancialSchedulerValues,
    w2InfoByWorkSiteWorkLocation,
    isW2Assignment,
    isAdmin,
  } = useContext(FinancialSchedulerContext);
  const formik: FormikProps<FinancialInfoFormValues> =
    useFormikContext<FinancialInfoFormValues>();
  const values = formik.values;
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const otPayRateFieldRef: any = useRef();

  const financialWarningMessages = getFinancialWarningMessages({
    mspVmsValues: mspOrVmsOverviewInfo,
    hasW2InfoByWorkSiteWorkLocation:
      !!w2InfoByWorkSiteWorkLocation ||
      isNil(workSiteWorkLocation?.state) ||
      !isW2Assignment,
  });

  const financialWarningMessageExists = !!financialWarningMessages.length;

  const getSafeComputedValue = (value: number) => {
    return financialWarningMessageExists ? 0 : getDecimalSafeValue(value);
  };

  const mbpPortionFormValue = getSafeComputedValue(
    FinancialInfoCalculator.getMbpPortionValue(
      values?.payRate,
      values?.mbpRate,
    ),
  );

  useDidUpdate(() => {
    calculateProfitRateValue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workSiteWorkLocation?.state, mspOrVmsOverviewInfo]);

  useDidUpdate(() => {
    calculateDTPayRate(false);
    calculateOvertimePayRate(false);
    // eslint-disable-next-line
  }, [isViewDTBillRateDtPayRateFields]);

  const calculateDTPayRate = (dtRateTouched: boolean = true) => {
    if (!isViewDTBillRateDtPayRateFields) return;

    setTimeout(() => {
      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;
      const { payRate } = values;

      const value = bankersRounding((payRate || 0) * DT_PAY_RATE_MULTIPLIER, 2);

      setFieldValue('dtPayRate', value);
      setFieldTouched('dtPayRate', dtRateTouched);
    }, 0);
  };

  const calculatePayRateChange = () => {
    calculateProfitRateValue();
    calculateDTPayRate();
    calculateOvertimePayRate();
  };

  const calculateOvertimePayRate = (overtimePayRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;
      const { payRate, overtime } = values;

      if (overtime === OvertimeEnum.Exempt) return;

      const value = bankersRounding((payRate || 0) * OVERTIME_MULTIPLIER, 2);

      setFieldValue('overtimePayRate', value);
      setFieldTouched('overtimePayRate', overtimePayRateTouched);
    }, 0);
  };

  const calculatePayRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasPayRateCustomLogic = (ptoMultiRateObject as MultiRateFormValues)
        ?.payCodesInfo?.[0]?.hasPayRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasPayRateCustomLogic
      )
        return null;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].payRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculatePayRatePTOValue(
        values.payRate,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const calculateBIllRatePTOMultiRateValue = (
    ptoMultiRateTouched: boolean = true,
  ) => {
    setTimeout(() => {
      if (!formRef.current) return null;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      if (!values.multiRates?.length) return null;

      const { ptoMultiRateIndex, ptoMultiRateObject } = findPTOMultiRate(
        values.multiRates,
      );

      const hasBillRateCustomLogic = (ptoMultiRateObject as MultiRateFormValues)
        ?.payCodesInfo?.[0]?.hasBillRateCustomLogic;

      if (
        ptoMultiRateIndex === -1 ||
        isNil(ptoMultiRateObject) ||
        hasBillRateCustomLogic
      )
        return null;

      const memberBilledIndicator =
        ptoMultiRateObject?.payCodesInfo?.[0]?.memberBilledIndicator;

      const fieldName = `${MULTI_RATES_KEY}[${ptoMultiRateIndex}].payCodesInfo[0].billRate`;
      const ptoPayTypeValue = FinancialInfoCalculator.calculateBillRatePTOValue(
        values.billRate,
        mbpPortionFormValue,
        memberBilledIndicator,
      );

      setFieldValue(fieldName, ptoPayTypeValue);
      setFieldTouched(fieldName, ptoMultiRateTouched, false);
    }, 0);
  };

  const calculateProfitRateValue = (profitRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;
      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      // Reset profitRate if billRate ot payRate is not provided
      const shouldResetValue = isNil(values.billRate) || isNil(values.payRate);
      const profitRateValue = FinancialInfoCalculator.getProfitRateValue(
        values,
        mspOrVmsOverviewInfo,
      );
      const safeValue = getSafeComputedValue(profitRateValue);

      setFieldValue('profitRate', shouldResetValue ? 0 : safeValue);
      setFieldTouched('profitRate', profitRateTouched);
    }, 0);
  };

  const handleOvertimeChange = (value: OvertimeEnum) => {
    calculateOvertimePayRate(false);
    setOvertimeValue(value);

    setTimeout(() => {
      if (value === OvertimeEnum.Exempt) {
        formRef.current.setFieldTouched('dtPayRate', false);
        formRef.current.setFieldTouched('dtBillRate', false);
        formRef.current.setFieldTouched('overtimePayRate', false);
        formRef.current.setFieldTouched('overtimeBillRate', false);

        formRef.current.setFieldValue('dtPayRate', null);
        formRef.current.setFieldValue('dtBillRate', null);
        formRef.current.setFieldValue('overtimePayRate', null);
        formRef.current.setFieldValue('overtimeBillRate', null);
      }
    }, 0);
  };

  const calculateMBPRateValue = (mbpRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!formRef.current) return;

      const { values, setFieldValue, setFieldTouched } =
        formRef.current as FormikProps<FinancialInfoFormValues>;

      // The profitRate depends on the mbpRate, because need to update this field too.
      const shouldResetValue = isNil(values.billRate) || isNil(values.payRate);
      const payRateValue = FinancialInfoCalculator.getProfitRateValue(
        values,
        mspOrVmsOverviewInfo,
      );
      const safeValue = getSafeComputedValue(payRateValue);

      setFieldValue('profitRate', shouldResetValue ? 0 : safeValue);
      setFieldValue('mbpRate', values.mbpRate);
      setFieldTouched('mbpRate', mbpRateTouched);
    }, 0);
  };

  const handleBillRateChanged = () => {
    calculateProfitRateValue();
    calculateBIllRatePTOMultiRateValue(false);
  };

  const handleBasePayRateChanged = () => {
    calculatePayRateChange();
    calculatePayRatePTOMultiRateValue();
  };

  const renderChangesEffectiveDateBtn = () => {
    return (
      predefinedFinancialSchedulerValues && (
        <Col xs={24} md={12}>
          <div className={styles.changeBtn}>
            <Button
              className="with-icon"
              icon={<span className="icon icon-edit" />}
              onClick={handleOpenFinancialInfoSchedulerModal}
            >
              Changes effective as of{' '}
              {moment(predefinedFinancialSchedulerValues.effectiveDate).format(
                'MM/DD/YYYY',
              )}
            </Button>
          </div>
        </Col>
      )
    );
  };

  return (
    <>
      <Row gutter={40}>
        {isW2Assignment && (
          <Col xs={24} md={12}>
            <FormItem name="payType" label="Pay type *">
              <Select name="payType" placeholder="Select pay type">
                {getPayTypeOptions().map((payType) => (
                  <Select.Option
                    key={`select-payType-${payType.value}`}
                    value={payType.value || ''}
                  >
                    {payType.label}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>
          </Col>
        )}

        <Col xs={24} md={12}>
          <FormItem label="Base pay rate *" name="payRate">
            <InputNumber
              name="payRate"
              placeholder="0.00"
              disabled={has(
                predefinedFinancialSchedulerValues?.financialInfoScheduler,
                'payRate',
              )}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
              onValueChange={() => handleBasePayRateChanged()}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem label="Bill rate *" name="billRate">
            <InputNumber
              name="billRate"
              placeholder="0.00"
              disabled={has(
                predefinedFinancialSchedulerValues?.financialInfoScheduler,
                'billRate',
              )}
              className="full-width"
              precision={2}
              inputPrefix={'$'}
              onValueChange={() => handleBillRateChanged()}
            />
          </FormItem>
        </Col>
        {!isTeammate && (
          <Col xs={24} md={12}>
            <FormItem label="Profit rate" name="profitRate">
              <InputNumber
                disabled
                allowNegative
                name="profitRate"
                placeholder="0.00"
                className="full-width"
                precision={2}
              />
            </FormItem>
          </Col>
        )}
        <Col xs={24} md={12}>
          <FormItem name="overtime" label="Overtime *">
            <Select
              name="overtime"
              placeholder="Select overtime type"
              onChange={handleOvertimeChange}
            >
              {getOvertimeOptions().map((overtime) => (
                <Select.Option
                  key={`select-overtime-${overtime.value}`}
                  value={overtime.value || ''}
                >
                  {overtime.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        {values?.overtime !== OvertimeEnum.Exempt && (
          <>
            <Col xs={24} md={12} ref={otPayRateFieldRef}>
              <FormItem
                label={
                  <>
                    <span>OT pay rate *</span>
                    <Tooltip
                      overlayClassName={cx(
                        'light-tooltip',
                        styles.modalTooltip,
                      )}
                      title="By default, the OT rate is 1.5x."
                      trigger="click"
                      placement="topLeft"
                      getPopupContainer={() => otPayRateFieldRef.current}
                      className={styles.modalTooltipOpener}
                    >
                      <span className="icon icon-attention-filled" />
                    </Tooltip>
                  </>
                }
                name="overtimePayRate"
              >
                <InputNumber
                  name="overtimePayRate"
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem label="OT bill rate *" name="overtimeBillRate">
                <InputNumber
                  name="overtimeBillRate"
                  placeholder="0.00"
                  disabled={has(
                    predefinedFinancialSchedulerValues?.financialInfoScheduler,
                    'overtimeBillRate',
                  )}
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                />
              </FormItem>
            </Col>
          </>
        )}
        {isViewDTBillRateDtPayRateFields && (
          <>
            <Col xs={24} md={12}>
              <FormItem name="dtPayRate" label="DT Pay rate">
                <InputNumber
                  disabled
                  name="dtPayRate"
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={12}>
              <FormItem name="dtBillRate" label="DT Bill rate *">
                <InputNumber
                  name="dtBillRate"
                  placeholder="0.00"
                  disabled={has(
                    predefinedFinancialSchedulerValues?.financialInfoScheduler,
                    'dtBillRate',
                  )}
                  className="full-width"
                  precision={2}
                  inputPrefix={'$'}
                />
              </FormItem>
            </Col>
          </>
        )}
        {renderChangesEffectiveDateBtn()}
      </Row>

      {financialWarningMessageExists && (
        <>
          {financialWarningMessages.map((message, index) => (
            <div
              key={`financial-message-${index}`}
              className={cx('hidden-md-up', styles.hintSm)}
            >
              {message}
            </div>
          ))}
        </>
      )}

      <Row gutter={40}>
        <Col xs={24} md={12}>
          <div className="modal-title">Rates Calculator</div>

          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="vmsMspRate" label="VMS/MSP Rate">
                <InputNumber
                  disabled
                  value={getFormattedNumber(
                    getSafeComputedValue(
                      FinancialInfoCalculator.getVmsMspRateValue(
                        mspOrVmsOverviewInfo,
                      ),
                    ),
                  )}
                  allowNegative
                  name="vmsMspRate"
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                />
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="VMS/MSP Portion" name="vmsMspPortion">
                <InputNumber
                  disabled
                  value={getSafeComputedValue(
                    FinancialInfoCalculator.getVmsMspPortionValue(
                      values?.billRate,
                      mspOrVmsOverviewInfo,
                    ),
                  )}
                  allowNegative
                  name="vmsMspPortion"
                  inputPrefix="$"
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                />
              </FormItem>
            </Col>
            {!isTeammate && (
              <>
                <Col xs={24} lg={12}>
                  <FormItem label="MBP Rate" name="mbpRate">
                    <InputNumber
                      disabled={!isAdmin}
                      name="mbpRate"
                      placeholder="0.00"
                      className="full-width"
                      precision={2}
                      onValueChange={() => calculateMBPRateValue()}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} lg={12}>
                  <FormItem label="MBP Portion" name="mbpPortion">
                    <InputNumber
                      disabled
                      value={mbpPortionFormValue}
                      allowNegative
                      name="mbpPortion"
                      inputPrefix={'$'}
                      placeholder="0.00"
                      className="full-width"
                      precision={2}
                    />
                  </FormItem>
                </Col>
              </>
            )}

            <Col xs={24} lg={12}>
              {checkIsCountriesWithStatesDropdown(
                workSiteWorkLocation?.country,
              ) ? (
                <FormItem label="Work State" name="workState">
                  <FormikSelectWithLoading
                    isLoading={isStatesLoading}
                    disabled
                    name="workState"
                    showSearch
                    showArrow={false}
                    optionFilterProp="children"
                    value={workSiteWorkLocation?.state || undefined}
                  >
                    {states.map((state) => (
                      <FormikSelectWithLoading.Option
                        key={`select-work-state-${state.id}`}
                        value={state.id || -1}
                      >
                        {state.name}
                      </FormikSelectWithLoading.Option>
                    ))}
                  </FormikSelectWithLoading>
                </FormItem>
              ) : (
                <FormItem label="Work State" name="workInternationalState">
                  <Input
                    name="workInternationalState"
                    disabled
                    value={
                      workSiteWorkLocation?.internationalStateName || undefined
                    }
                  />
                </FormItem>
              )}
            </Col>
          </Row>
        </Col>

        <Divider className="modal-divider after-form hidden-md-up" />

        <Col xs={24} md={12}>
          <div className="modal-title">Markup Calculator</div>

          <Row gutter={20}>
            {!isTeammate && (
              <Col xs={24} lg={12}>
                <FormItem name="marginPercent" label="Margin %">
                  <InputNumber
                    disabled
                    value={getSafeComputedValue(
                      FinancialInfoCalculator.getMarginPercentValue(
                        values,
                        mspOrVmsOverviewInfo,
                      ),
                    )}
                    allowNegative
                    name="marginPercent"
                    placeholder="0.00"
                    className="full-width"
                    precision={2}
                  />
                </FormItem>
              </Col>
            )}
            <Col xs={24} lg={12}>
              <FormItem label="Markup %" name="markupPercent">
                <InputNumber
                  disabled
                  value={getSafeComputedValue(
                    FinancialInfoCalculator.getMarkupPercentValue(values),
                  )}
                  allowNegative
                  name="markupPercent"
                  placeholder="0.00"
                  className="full-width"
                  precision={2}
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>

      {financialWarningMessageExists && (
        <ul className={cx('hidden-md-down icon-list', styles.hintMd)}>
          {financialWarningMessages.map((message, index) => (
            <li key={`financial-message-${index}`}>
              <span className="icon icon-error" />
              <span>{message}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
